import type { JSX } from 'react';
import './MobileNav.css';

export type ConsoleView = 'sessions' | 'chat' | 'memory' | 'tools' | 'files' | 'jobs';

interface MobileNavProps {
  active: ConsoleView;
  onChange: (view: ConsoleView) => void;
  /** Whether the Jobs tab renders — only for agents with workflow tools
   *  (Vera). Keeps other consoles at the original five tabs. */
  showJobs?: boolean;
}

interface NavItem {
  view: ConsoleView;
  label: string;
  icon: JSX.Element;
}

const ITEMS: readonly NavItem[] = [
  {
    view: 'sessions',
    label: 'Sessions',
    icon: (
      <path
        d="M4 6h16M4 12h16M4 18h10"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    ),
  },
  {
    view: 'chat',
    label: 'Chat',
    icon: (
      <path
        d="M5 5h14a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H9l-4 4V6a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    ),
  },
  {
    view: 'memory',
    label: 'Memory',
    icon: (
      <>
        <circle cx="6" cy="8" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="18" cy="7" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="13" cy="17" r="2.4" stroke="currentColor" strokeWidth="1.6" />
        <path d="M8.3 9.2 11 15.4M15.8 8.4 14.4 14.7" stroke="currentColor" strokeWidth="1.4" />
      </>
    ),
  },
  {
    view: 'tools',
    label: 'Tools',
    icon: (
      <path
        d="M14.7 6.3a3.5 3.5 0 0 0-4.8 4.5l-4.6 4.6a1.5 1.5 0 0 0 2.1 2.1l4.6-4.6a3.5 3.5 0 0 0 4.5-4.8l-2 2-1.9-1.9 2-1.9Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    ),
  },
  {
    view: 'files',
    label: 'Files',
    icon: (
      <path
        d="M5 5h4l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    ),
  },
  {
    // Jobs (workflows + schedules) — approved mockup rev 5 naming; the
    // sixth tab, present only when the console's agent has workflow tools.
    view: 'jobs',
    label: 'Jobs',
    icon: (
      <path
        d="M12 4v4m0 8v4M4 12h4m8 0h4M6.8 6.8l2.8 2.8m4.8 4.8 2.8 2.8m0-10.4-2.8 2.8m-4.8 4.8-2.8 2.8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    ),
  },
];

/** Bottom tab bar (portrait only) — selects which single panel is visible. */
export default function MobileNav({ active, onChange, showJobs = false }: MobileNavProps) {
  const items = showJobs ? ITEMS : ITEMS.filter((i) => i.view !== 'jobs');
  return (
    <nav className="om-mobilenav">
      {items.map((item) => (
        <button
          type="button"
          key={item.view}
          className={item.view === active ? 'is-active' : undefined}
          aria-label={item.label}
          aria-pressed={item.view === active}
          onClick={() => onChange(item.view)}
        >
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
            {item.icon}
          </svg>
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}
